import { useState } from "react";

import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectorySearchHelper } from "@/ContactDirectory/Helpers/DirectorySearchHelper";
import { usePhoneNumberFormatter } from "@/ContactDirectory/Hooks/usePhoneNumberFormatter";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models//DirectoryFaxNumber";
import { DirectoryOtherEstablishment } from "@/ContactDirectory/Models//DirectoryOtherEstablishment";
import { DirectoryFormRow } from "../Common/DirectoryFormRow";
import { RemoveContainer } from "../Common/RemoveContainer";
import { DirectoryPopupController } from "../DirectoryPopupController";
import { FaxDirectorySearchComponent } from "../Search/FaxDirectorySearchComponent";
import { FaxDirectorySearchResultItem } from "../Search/FaxDirectorySearchResultItem";

import commonStyles from "../FaxDirectoryForm.module.scss";

interface IHealthcareProviderEstablishmentProps {
    establishment: DirectoryOtherEstablishment;
    faxNumber?: DirectoryFaxNumber;
    onSelect: (establishment: DirectoryOtherEstablishment, faxNumber: DirectoryFaxNumber) => void;
    onRemove?: (id: string, establishmentId: string) => void;
    isValid?: boolean;
    linkedFaxNumbers?: {
        selectedLinkedFaxNumbers: DirectoryFaxNumber[];
        entityFaxNumber: DirectoryFaxNumber;
    }
    shouldHideRemoveButton?: boolean;
}

export function HealthcareProviderEstablishment(props: IHealthcareProviderEstablishmentProps): JSX.Element {
    const { getText } = useTranslation();
    const [state, setState] = useState<{ establishment: DirectoryOtherEstablishment | null, fax: DirectoryFaxNumber | null }>(() => ({
        establishment: props.establishment,
        fax: props.faxNumber
    }));
    const [isAddPopupOpen, setIsAddPopupOpen] = useState<boolean>(false);

    const formatter = usePhoneNumberFormatter(state.establishment?.address?.countryId, state.establishment?.address?.stateId);

    function firstOrDefaultFaxNumber(entity: DirectoryOtherEstablishment, faxNumberId?: string | null): DirectoryFaxNumber {
        const faxNumbers = entity.faxNumbers;
        const selectedFaxNumber = faxNumbers.firstOrDefault(p => p.id === faxNumberId);

        if (faxNumberId && selectedFaxNumber && !props.linkedFaxNumbers.selectedLinkedFaxNumbers.some(p => p.id === faxNumberId))
            return selectedFaxNumber;

        const defaultSelectedFaxNumber = entity.getDefaultFaxNumberToSelect();
        if (defaultSelectedFaxNumber && !defaultSelectedFaxNumber.isDuplicate(props.linkedFaxNumbers.selectedLinkedFaxNumbers))
            return defaultSelectedFaxNumber;

        return faxNumbers.sortByProperty(p => p.id).firstOrDefault(faxNumber =>
            !faxNumber.isDuplicate(props.linkedFaxNumbers.selectedLinkedFaxNumbers)
        );
    }

    function onSelectEntity(item: FaxDirectorySearchResultItem): void {
        const establishment = item.entity as DirectoryOtherEstablishment;
        const firstDefaultNumber = firstOrDefaultFaxNumber(establishment, item.selectedFaxNumberId);
        establishment.selectedLinkedFaxNumber = firstDefaultNumber;
        setState({ establishment, fax: firstDefaultNumber });
        props.onSelect(establishment, firstDefaultNumber);
        setIsAddPopupOpen(false);
    }

    return (
        <RemoveContainer
            onRemove={props.shouldHideRemoveButton ? null : () => props.onRemove(state.fax?.id, state.establishment.id)}
            className="margin-md-bottom"
        >
            <>
                {state.establishment?.id
                    ? (
                        <DirectoryFormRow labelKey="EstablishmentLabel">
                            <div className="blue bold">{state.establishment.name}</div>
                            <div>{DirectorySearchHelper.formatAddress(state.establishment.address)}</div>
                            <div>{DirectorySearchHelper.formatPhoneNumbers(state.establishment.phoneNumbers.map(p => formatter(p)))}</div>
                            <div className="bold">{getText("FaxNumberLabel")}</div>
                            {state.establishment.faxNumbers?.any() && (
                                <InlineOptionsListControl
                                    id={`linked-establishment-fax-number-selection-${state.establishment.id}`}
                                    items={state.establishment.faxNumbers.sortByProperty(p => p.id).map(p => {
                                        const isDisabled = p.isDuplicate(props.linkedFaxNumbers.selectedLinkedFaxNumbers);

                                        return new InlineOption(
                                            p.id,
                                            <ul>
                                                <li>{formatter(p)}</li>
                                                <li>{p.description}</li>
                                                {p.isObsoleteFaxNumberLink && (
                                                    <li className={commonStyles.dangerColor}>
                                                        {getText("ContactDirectory_OutdatedFaxNumberLinkWarning")}
                                                    </li>
                                                )}
                                            </ul>,
                                            isDisabled
                                        );
                                    })}
                                    onChange={(item: InlineOption<any>) => {
                                        const fax = state.establishment.faxNumbers.firstOrDefault(p => p.id == item?.Key && !item.Disabled);
                                        setState({ ...state, fax: fax });
                                        props.onSelect(state.establishment, fax);
                                    }}
                                    value={state.fax?.id}
                                    isMultipleLines
                                    style={{ marginTop: "6px", alignItems: "flex-start" }}
                                    inputStyle={{ marginTop: "3px" }}
                                    labelStyle={{ width: "100%" }}
                                />
                            )}
                        </DirectoryFormRow>
                    )
                    : (
                        <div className={commonStyles.removeableRightSpacing}>
                            <DirectoryFormRow labelKey="SearchLabel">
                                <FaxDirectorySearchComponent
                                    entityTypes={[DirectoryEntityType.Other]}
                                    onItemSelected={other => onSelectEntity(other)}
                                    isValid={props.isValid}
                                    linkedFaxNumbers={props.linkedFaxNumbers.selectedLinkedFaxNumbers}
                                    dataCy="contactDirectory-otherEstablishmentSearch"
                                    scrollToSearchResult
                                />
                            </DirectoryFormRow>
                            <a className={commonStyles.link} onClick={() => setIsAddPopupOpen(true)}>
                                {getText("AddNewLabel")}
                            </a>
                            {isAddPopupOpen && (
                                <DirectoryPopupController
                                    onSave={other => onSelectEntity(new FaxDirectorySearchResultItem({ entity: other }))}
                                    onClose={() => setIsAddPopupOpen(false)}
                                    editModel={null}
                                    defaultType={DirectoryEntityType.Other}
                                    onEntityUseInstead={other => onSelectEntity(new FaxDirectorySearchResultItem({ entity: other }))}
                                    onModify={other => onSelectEntity(new FaxDirectorySearchResultItem({ entity: other }))}
                                />
                            )}
                        </div>
                    )
                }
            </>
        </RemoveContainer>
    );
}
